import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { CalendarClock, ChevronDown, ChevronUp } from 'lucide-react';

import GenericSelect from '../../../components/ui/GenericSelect';
import Button from '../../../components/ui/Button';
import PanelCard from '../../performance-operations/components/PanelCard';
import {
  Badge, Footer, Grid, KpiSub, KpiValue, ProgressFill, ProgressTrack, Sub, Table,
} from '../../performance-operations/styles';
import { formatNumber, hasValue, metricOf, usePanel } from '../../performance-operations/config';
import { KIND_META } from '../../performance-inbox/config';
import { formatDate, formatDateTime } from '../../../utils/Utils';
import {
  InfoLine, ListRow, RowButton, RowMain, RowTitle, SectionLabel,
} from '../../performance-me/styles';
import { stageLabel } from '../../performance-me/config';
import {
  DISTRIBUTION_CODES, INBOX_PATH, KPI_CODES, RESULT_ACK_STAGE, TEAM_GOALS_PATH, TODAY_LIMIT, defaultStage, fetchAgenda,
  fetchMyInbox, fetchStageCoverage, fetchTeamMetrics, sortTodo, teamAnalyticsFilters,
} from '../config';

// PM Analitik Faz 2 (spec §9.2) — E17 Özet: KPI'lar (Açık Görev, Geciken, Aşama Kapsaması, Sonuç Paylaşılan, Sonucu Gördü) metrik
// ucundan ekip süzgeciyle; Bugün Yapılacaklar = Gelen Kutum; orta sağ = agenda ucu; Puan Dağılımı katlanabilir (varsayılan kapalı,
// küçük grupta sunucu gizler). Karşılaştırma yok (§3.3 scope). Panel başına bağımsız yükleme/hata/Tekrar Dene.
const pct = (m, lang) => (hasValue(m) ? `${formatNumber(m.value, lang)}%` : null);
const ratio = (t, m, lang) => (hasValue(m) && m.numerator != null && m.denominator != null
  ? t('performance.operations.kpi.ratio', { num: formatNumber(m.numerator, lang, 0), den: formatNumber(m.denominator, lang, 0) })
  : null);

export default function TeamSummary({ filters, context }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const history = useHistory();
  const analytics = useMemo(() => teamAnalyticsFilters(filters, context?.managerEmployeeId), [filters, context?.managerEmployeeId]);
  const key = JSON.stringify(analytics);
  const enabled = !!filters.periodId && !!context?.managerEmployeeId;

  const kpi = usePanel(() => fetchTeamMetrics(analytics, KPI_CODES), [key], enabled);
  const stages = usePanel(() => fetchStageCoverage(analytics), [key], enabled);
  const agenda = usePanel(() => fetchAgenda(filters), [filters.periodId, filters.team], enabled);
  const todo = usePanel(() => fetchMyInbox(), []);
  const [stageChoice, setStageChoice] = useState(null);
  const [distributionOpen, setDistributionOpen] = useState(false);
  const distribution = usePanel(() => fetchTeamMetrics(analytics, DISTRIBUTION_CODES), [key], enabled && distributionOpen);

  const m = (code) => metricOf(kpi.data, code);
  const kpiCommon = { loading: kpi.loading, error: kpi.error, onRetry: kpi.retry };
  const stageCommon = { loading: stages.loading, error: stages.error, onRetry: stages.retry };
  const cells = (stages.data?.cells || []).filter((c) => c.key);
  const stageOptions = cells.filter((c) => c.key !== RESULT_ACK_STAGE).map((c) => ({ id: c.key, name: stageLabel(t, c.key) }));
  const selectedStage = stageChoice && stageOptions.some((o) => o.id === stageChoice) ? stageChoice : defaultStage(cells);
  const stageMetric = (stageKey) => {
    const cell = cells.find((c) => c.key === stageKey);
    return cell ? (metricOf(cell.metrics, 'M03') || cell.metric) : null;
  };
  const coverage = stageMetric(selectedStage);
  const seen = stageMetric(RESULT_ACK_STAGE);   // result_ack aşaması dönem kapanmadan doğmaz → "Veri Yok"
  const noData = t('performance.operations.kpi.noData');

  const todoItems = sortTodo(todo.data || []).slice(0, TODAY_LIMIT);
  const meetings = agenda.data?.upcomingMeetings || [];
  const stale = agenda.data?.staleGoals || {};
  const staleMetric = stale.metric || null;

  const high = metricOf(distribution.data, 'M07');
  const low = metricOf(distribution.data, 'M08');
  const suppressed = !!(high?.suppressionReason || low?.suppressionReason);
  const bands = hasValue(high) && hasValue(low)
    ? [
      { key: 'high', value: Number(high.value) },
      { key: 'mid', value: Math.max(0, Math.round((100 - Number(high.value) - Number(low.value)) * 100) / 100) },
      { key: 'low', value: Number(low.value) },
    ]
    : [];
  const sampleN = high?.sampleN ?? low?.sampleN ?? null;

  return (
    <Grid data-summary="team">
      <PanelCard panelKey="kpi-openTasks" title={t('performance.workspace.team.kpi.openTasks')} span={3} compact {...kpiCommon}>
        <KpiValue data-kpi="pm.tasksOpen">{hasValue(m('pm.tasksOpen')) ? formatNumber(m('pm.tasksOpen').value, lang, 0) : noData}</KpiValue>
        <KpiSub>{t('performance.workspace.team.kpi.openTasksSub')}</KpiSub>
      </PanelCard>
      <PanelCard panelKey="kpi-overdue" title={t('performance.workspace.team.kpi.overdue')} span={3} compact {...kpiCommon}>
        <KpiValue data-kpi="M04">{hasValue(m('M04')) ? formatNumber(m('M04').value, lang, 0) : noData}</KpiValue>
        {m('M04')?.numerator != null ? (
          <KpiSub>{t('performance.workspace.team.kpi.overduePeople', { people: formatNumber(m('M04').numerator, lang, 0) })}</KpiSub>
        ) : null}
      </PanelCard>
      <PanelCard
        panelKey="kpi-stageCoverage" title={t('performance.workspace.team.kpi.stageCoverage')} span={2} compact {...stageCommon}
        empty={stageOptions.length === 0} emptyText={noData}
      >
        <KpiValue data-kpi="M03">{pct(coverage, lang) || noData}</KpiValue>
        {ratio(t, coverage, lang) ? <KpiSub>{ratio(t, coverage, lang)}</KpiSub> : null}
        <GenericSelect
          value={selectedStage}
          onChange={(v) => setStageChoice(v)}
          options={stageOptions}
          idKey="id"
          labelKey="name"
          clearable={false}
          size="small"
        />
      </PanelCard>
      <PanelCard panelKey="kpi-published" title={t('performance.workspace.team.kpi.published')} span={2} compact {...kpiCommon}>
        <KpiValue data-kpi="M49">{pct(m('M49'), lang) || noData}</KpiValue>
        {ratio(t, m('M49'), lang) ? <KpiSub>{ratio(t, m('M49'), lang)}</KpiSub> : null}
      </PanelCard>
      <PanelCard panelKey="kpi-seen" title={t('performance.workspace.team.kpi.seen')} span={2} compact {...stageCommon}>
        <KpiValue data-kpi="result_ack">{pct(seen, lang) || noData}</KpiValue>
        {ratio(t, seen, lang) ? <KpiSub>{ratio(t, seen, lang)}</KpiSub> : null}
      </PanelCard>

      <PanelCard
        panelKey="today" title={t('performance.workspace.team.today.title')} span={7}
        loading={todo.loading} error={todo.error} onRetry={todo.retry}
        empty={todoItems.length === 0} emptyText={t('performance.workspace.team.today.empty')}
      >
        {todoItems.map((item, index) => {
          const meta = KIND_META[item.kind] || {};
          // Bağlantı sunucudan, kayda özgü (Gelen Kutum ile aynı — FE override yok).
          const link = item.link || null;
          const content = (
            <>
              <RowTitle>{`${t(`performance.inbox.kind.${item.kind}`, { defaultValue: item.kind })} · ${item.title}`}</RowTitle>
              {item.subtitle && !meta.subtitle ? <Sub>{item.subtitle}</Sub> : null}
              {meta.subtitle ? <Sub>{meta.subtitle(item, t)}</Sub> : null}
            </>
          );
          // Gelen Kutum kuralı (müşteri geri bildirimi 2026-10-28): aksiyonu olmayan satır tıklanır
          // GÖRÜNMEZ ve hiçbir yere gitmez — MeSummary ile aynı (RowMain, data-static).
          return (
            <ListRow key={`${item.kind}-${index}`} data-todo={item.kind}>
              {link
                ? <RowButton onClick={() => history.push(link)}>{content}</RowButton>
                : <RowMain data-static="1">{content}</RowMain>}
              {item.dueAt ? <Badge $tone={item.overdue ? 'danger' : undefined}>{formatDate(item.dueAt, lang)}</Badge> : null}
            </ListRow>
          );
        })}
        <Footer>
          <Button variant="ghost" onClick={() => history.push(INBOX_PATH)} data-action="inbox">{t('performance.workspace.team.today.all')}</Button>
        </Footer>
      </PanelCard>

      <PanelCard panelKey="agenda" title={t('performance.workspace.team.agenda.title')} span={5} loading={agenda.loading} error={agenda.error} onRetry={agenda.retry}>
        <SectionLabel>{t('performance.workspace.team.agenda.meetings')}</SectionLabel>
        {meetings.length === 0 ? <Sub>{t('performance.workspace.team.agenda.noMeetings')}</Sub> : null}
        {meetings.map((mt) => (
          <ListRow key={mt.id} data-meeting={mt.employeeId}>
            <RowMain><RowTitle>{mt.employeeName}</RowTitle></RowMain>
            <InfoLine><CalendarClock size={14} aria-hidden="true" /><Sub>{formatDateTime(mt.scheduledAt, lang)}</Sub></InfoLine>
          </ListRow>
        ))}

        <SectionLabel>{t('performance.workspace.team.agenda.approvals')}</SectionLabel>
        <ListRow data-approvals={agenda.data?.pendingGoalApprovals ?? 0}>
          <RowMain><RowTitle>{t('performance.workspace.team.agenda.pendingApprovals', { value: agenda.data?.pendingGoalApprovals ?? 0 })}</RowTitle></RowMain>
          <Button variant="ghost" onClick={() => history.push(TEAM_GOALS_PATH)} data-action="approvals">{t('performance.workspace.team.agenda.openApprovals')}</Button>
        </ListRow>

        <SectionLabel>{t('performance.workspace.team.agenda.staleGoals')}</SectionLabel>
        <Sub data-m62="1">
          {staleMetric?.suppressionReason
            ? t('performance.workspace.team.distribution.suppressed')
            : t('performance.workspace.team.agenda.staleMetric', {
              value: pct(staleMetric, lang) || noData,
              ratio: ratio(t, staleMetric, lang) || '—',
            })}
        </Sub>
        {(stale.people || []).length === 0 ? <Sub>{t('performance.workspace.team.agenda.noStale')}</Sub> : null}
        {(stale.people || []).map((p) => (
          <ListRow key={p.employeeId} data-stale-person={p.employeeId}>
            <RowMain><RowTitle>{p.fullName}</RowTitle></RowMain>
            <Badge $tone="danger">{t('performance.workspace.team.agenda.staleCount', { value: p.staleGoals })}</Badge>
          </ListRow>
        ))}
      </PanelCard>

      <PanelCard
        panelKey="distribution" title={t('performance.workspace.team.distribution.title')} span={12}
        loading={distributionOpen && distribution.loading} error={distributionOpen && distribution.error} onRetry={distribution.retry}
        empty={distributionOpen && !suppressed && bands.length === 0} emptyText={noData}
        actions={(
          <Button variant="ghost" onClick={() => setDistributionOpen((v) => !v)} data-action="toggle-distribution" aria-expanded={distributionOpen ? 'true' : 'false'}>
            {distributionOpen ? <ChevronUp size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />}
            {' '}
            {distributionOpen ? t('performance.workspace.team.distribution.hide') : t('performance.workspace.team.distribution.show')}
          </Button>
        )}
      >
        {distributionOpen && suppressed ? <Sub data-state="suppressed">{t('performance.workspace.team.distribution.suppressed')}</Sub> : null}
        {distributionOpen && !suppressed && bands.length > 0 ? (
          <>
            <Table>
              <thead>
                <tr>
                  <th>{t('performance.workspace.team.distribution.band')}</th>
                  <th>{t('performance.workspace.team.distribution.share')}</th>
                  <th aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {bands.map((b) => (
                  <tr key={b.key} data-band={b.key}>
                    <td>{t(`performance.workspace.team.distribution.${b.key}`)}</td>
                    <td>{`${formatNumber(b.value, lang)}%`}</td>
                    <td><ProgressTrack><ProgressFill $pct={b.value} /></ProgressTrack></td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {sampleN != null ? <Sub>{t('performance.workspace.team.distribution.sample', { value: sampleN })}</Sub> : null}
          </>
        ) : null}
      </PanelCard>
    </Grid>
  );
}
